import { useEffect, useId, useRef, type ReactNode } from 'react';
import { modalStack } from '../../lib/modalStack';
import { useTranslation } from 'react-i18next';

interface ModalProps {
  /** Heading text; also the dialog's accessible name. */
  title: ReactNode;
  /** Optional line under the heading; becomes the accessible description. */
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Tailwind max-width class for the panel. */
  widthClass?: string;
  zClass?: string;
  /** While true, Escape and backdrop clicks don't close (e.g. a save is in flight). */
  locked?: boolean;
}

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Accessible modal shell: `role="dialog"` + `aria-modal`, named by its title, Escape closes,
 * focus moves in on open (first focusable, unless a child already took it via autoFocus), Tab
 * stays inside, and focus returns to the opener on close. The backdrop is a real button so
 * click-to-close is reachable without a mouse-only handler.
 */
export function Modal({
  title,
  description,
  onClose,
  children,
  widthClass = 'max-w-md',
  zClass = 'z-[200]',
  locked = false,
}: ModalProps) {
  const titleId = useId();
  const descId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const closeRef = useRef(onClose);
  const lockedRef = useRef(locked);
  useEffect(() => {
    closeRef.current = onClose;
    lockedRef.current = locked;
  });

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      (panel.querySelector<HTMLElement>(FOCUSABLE) ?? panel).focus();
    }
    const stackId = modalStack.push();
    const onKey = (e: KeyboardEvent) => {
      // Only the topmost modal handles keys (each one listens on document).
      if (!modalStack.isTop(stackId)) return;
      if (e.key === 'Escape' && !lockedRef.current) {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      modalStack.remove(stackId);
      document.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, []);

  return (
    <div className={`fixed inset-0 ${zClass} flex items-center justify-center p-4`}>
      <button
        type="button"
        aria-label={t('dialog.close')}
        tabIndex={-1}
        className="absolute inset-0 bg-scrim cursor-default"
        onClick={() => {
          if (!locked) onClose();
        }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={`relative bg-surface rounded-lg shadow-pop w-full ${widthClass} max-h-[90vh] overflow-y-auto focus:outline-none`}
      >
        <div className="px-5 py-4 border-b border-line">
          <h2 id={titleId} className="text-lg font-semibold text-ink">
            {title}
          </h2>
          {description && (
            <p id={descId} className="text-xs text-muted mt-1">
              {description}
            </p>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}
